import React from 'react';
import { makeStyles } from 'tss-react/mui';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import { useRouter } from 'next/router';
import ErrorReporter from '../../../lib/ErrorReporter';

const useStyles = makeStyles()((theme) => ({
    message: {
        margin: theme.spacing(3),
        width: '100%',
    },
}));

const SignInError: React.FC<React.PropsWithChildren<{ title?: string }>> = ({ title = 'Whoops!', children }) => {
    const { classes } = useStyles();
    return (
        <Box className={classes.message}>
            <Alert severity="error">
                <AlertTitle>{title}</AlertTitle>
                {children}
            </Alert>
        </Box>
    );
};

const useGetSignInErrorMessage = (): null | string => {
    const { query } = useRouter();
    if (query.error === 'access_denied') {
        return 'Access was denied to login to the portal';
    } else if (query.error === 'agents_not_allowed') {
        return query.error;
    } else if (query.auth0 === 'callback' && typeof query.error_description === 'string') {
        ErrorReporter.captureMessage(query.error_description, {
            level: 'error',
            extra: {
                query,
            },
        });
        return query.error_description;
    } else if (query.auth0 === 'callback' && query.error) {
        ErrorReporter.captureMessage('Sorry, an unexpected login issue has occurred', {
            level: 'error',
            extra: {
                query,
            },
        });
        return 'Sorry, an unexpected login issue has occurred';
    }
    return null;
};

const SignInMessage: React.FC<React.PropsWithChildren<unknown>> = () => {
    const message = useGetSignInErrorMessage();

    if (!message) {
        return null;
    }

    return (
        <SignInError>
            {message === 'agents_not_allowed' ? (
                <>
                    <p>
                        <strong>You’re signed in with an Agent account.</strong>
                    </p>
                    <p>Agent accounts can’t access customer booking management here.</p>
                    <p>
                        Please use the <strong>Agent Booking Portal</strong> instead:
                    </p>
                    <p>
                        <a href="https://bookings.jucy.com">Go to Agent Booking Portal</a>
                    </p>
                </>
            ) : (
                <>
                    We were unable to sign you in.
                    <br />
                    {message}
                    <br />
                    <br />
                    Please try again.
                </>
            )}
        </SignInError>
    );
};

export default SignInMessage;
